// SPのボトムメニューの共通レイアウト

import { useEffect, useRef, useState } from 'react';

type Props = {
  initPositionY?: string; // スワイプ前のposition
  swipeAfterPositionY?: string; // スワイプ後のposition
  isSwipeAfter?: boolean; // 下から上にスワイプしたら表示領域が伸びる仕様で、伸びてる状態ならTrue
  children: React.ReactNode;
  handleCloseClick: () => void;
  onSwipeDown?: () => void;
  minHeight?: number;
};

const SpBottomMenuLayout = ({
  isSwipeAfter = false,
  children,
  handleCloseClick,
  onSwipeDown,
  minHeight,
}: Props) => {
  const touchStartY = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTranslateY, setCurrentTranslateY] = useState<string>('');
  const [finalTranslateY, setFinalTranslateY] = useState<string>('');
  const SWIPE_THRESHOLD = 200;

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    const measure = () => setContentHeight(el.scrollHeight);

    measure(); // 初回計測

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const MAX_HEIGHT = Math.floor(window.innerHeight * 0.7);
  const MIN_HEIGHT = minHeight || 60;
  const autoHeightPx = Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);

  const getContainerHeight = () => {
    if (isDragging && currentTranslateY) {
      return currentTranslateY;
    }
    if (finalTranslateY) {
      return finalTranslateY;
    }
    return `${isSwipeAfter ? MAX_HEIGHT : autoHeightPx}px`;
  };

  const initPositionYPx = autoHeightPx;
  const swipeAfterPositionYPx = MAX_HEIGHT;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndY.current = e.touches[0].clientY;

    if (touchStartY.current !== null && touchEndY.current !== null) {
      const deltaY = touchEndY.current - touchStartY.current;

      let basePositionPx: number;
      if (finalTranslateY) {
        basePositionPx = parseInt(finalTranslateY.replace('px', ''));
      } else {
        basePositionPx = isSwipeAfter ? swipeAfterPositionYPx : initPositionYPx;
      }

      let newPositionPx = basePositionPx - deltaY;
      newPositionPx = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newPositionPx));

      setCurrentTranslateY(`${newPositionPx}px`);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);

    if (touchStartY.current !== null && touchEndY.current !== null) {
      const deltaY = touchEndY.current - touchStartY.current;

      if (currentTranslateY) {
        setFinalTranslateY(currentTranslateY);
      }

      if (deltaY > SWIPE_THRESHOLD) {
        if (onSwipeDown) {
          onSwipeDown();
        } else {
          handleCloseClick();
        }
      }
    }

    touchStartY.current = null;
    touchEndY.current = null;
    setCurrentTranslateY('');
  };

  // 背面への長押しイベント伝播を防ぐハンドラー
  const handleContentTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // SpBottomMenuLayout内のすべてのタッチイベントの伝播を防ぐ
    e.stopPropagation();
  };

  const handleContentTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    // SpBottomMenuLayout内のすべてのタッチイベントの伝播を防ぐ
    e.stopPropagation();
  };

  const handleContentTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    // SpBottomMenuLayout内のすべてのタッチイベントの伝播を防ぐ
    e.stopPropagation();
  };

  return (
    <>
      <div className='fixed inset-0 z-[99999]'>
        <div
          className='absolute inset-0 bg-black/30'
          onClick={handleCloseClick}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCloseClick();
          }}
        ></div>
        <div
          className='fixed bottom-0 left-0 flex w-full flex-col rounded-t-[20px] bg-white'
          style={{
            height: getContainerHeight(),
            transition: isDragging ? 'none' : 'height 0.3s ease',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
          onTouchStart={handleContentTouchStart}
          onTouchEnd={handleContentTouchEnd}
          onTouchMove={handleContentTouchMove}
          onTouchCancel={handleContentTouchEnd}
        >
          <div
            className='flex justify-center pb-2 pt-3'
            style={{
              touchAction: 'none',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className='h-1 w-[100px] rounded-[4px] bg-gray-200' />
          </div>
          <div
            ref={contentRef}
            className='flex-1 overflow-hidden'
            style={{
              touchAction: 'auto',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default SpBottomMenuLayout;
